/**
 * Hooks do catálogo.
 *
 * São as listas que preenchem os filtros. `staleTime` longo de propósito:
 * dimensão muda quando alguém mexe no admin, não a cada navegação, e recarregar
 * a lista a cada troca de aba é gasto sem informação.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from './api'
import { chaves } from './query'
import type { BaseSuprimento, Cliente, Distribuidora, Praca, Produto } from './tipos'

const UMA_HORA = 60 * 60 * 1000

export function useProdutos() {
  return useQuery({
    queryKey: chaves.produtos,
    queryFn: () => api.obter<Produto[]>('/catalogo/produtos/'),
    staleTime: UMA_HORA,
  })
}

export function useClientes() {
  return useQuery({
    queryKey: chaves.clientes,
    queryFn: () => api.obter<Cliente[]>('/catalogo/clientes/'),
    staleTime: UMA_HORA,
  })
}

export function useBases() {
  return useQuery({
    queryKey: chaves.bases,
    queryFn: () => api.obter<BaseSuprimento[]>('/catalogo/bases/'),
    staleTime: UMA_HORA,
  })
}

export function usePracas() {
  return useQuery({
    queryKey: chaves.pracas,
    queryFn: () => api.obter<Praca[]>('/catalogo/pracas/'),
    staleTime: UMA_HORA,
  })
}

export function useDistribuidoras() {
  return useQuery({
    queryKey: chaves.distribuidoras,
    queryFn: () => api.obter<Distribuidora[]>('/catalogo/distribuidoras/'),
    staleTime: UMA_HORA,
  })
}

/**
 * Favorita ou desfavorita uma distribuidora.
 *
 * Única escrita do catálogo feita pelo app — o operador favorita do próprio
 * filtro. Invalida o contexto do Terra Brasil porque é ele que ordena as
 * favoritas primeiro no seletor.
 */
export function useFavoritarDistribuidora() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, favorita }: { id: number; favorita: boolean }) =>
      api.atualizar<Distribuidora>(`/catalogo/distribuidoras/${id}/favorita/`, { favorita }),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.distribuidoras })
      void cliente.invalidateQueries({ queryKey: chaves.tbContexto })
    },
  })
}
